// dotHours.js — the seven-day on-duty window.
//
// WHY THE DATES ARE STAMPED AND NOT COMPUTED
// ------------------------------------------
// The paper short form has seven UNLABELLED boxes. A person in the office
// knows what "box 3" means; software does not, and neither does anyone
// reading the file two years from now. So each box carries a real calendar
// date, and those dates are FROZEN the first time the driver reaches the
// step — never recomputed at render or at submit.
//
// If the window were derived from "now" at submission time, a driver who
// starts the form at 11:50pm and finishes at 12:10am would have every one of
// his seven answers silently shifted onto the wrong day. Same principle as
// the school-terminator rule: judge the data that was stored, never "now".
//
// The window is the seven days ENDING TODAY (today back through six days
// ago), because 49 CFR 395.3's 60-in-7 / 70-in-8 count includes the current
// day, and a driver at a pre-employment may well have run this morning.

export const DOT_HOURS_WINDOW_DAYS = 7;
export const DOT_HOURS_MAX_PER_DAY = 24;

const pad = (n) => String(n).padStart(2, '0');

/** Local-time YYYY-MM-DD. Deliberately NOT toISOString(), which is UTC and
 *  rolls the date over for anyone west of Greenwich after 7-8pm. */
export const localISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Build the seven-day window ending today, oldest first.
 * Returns [{ date: 'YYYY-MM-DD', hours: 0 }, ...] — seven entries.
 */
export const buildHoursWindow = (today = new Date()) => {
  const days = [];
  for (let offset = DOT_HOURS_WINDOW_DAYS - 1; offset >= 0; offset--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    d.setDate(d.getDate() - offset);
    days.push({ date: localISODate(d), hours: 0 });
  }
  return days;
};

/** 'Mon 8/11' — short enough for a phone, unambiguous enough for a file. */
export const formatDayLabel = (isoDate) => {
  const parts = String(isoDate || '').split('-');
  if (parts.length !== 3) return isoDate || '';
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (isNaN(d.getTime())) return isoDate;
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${dow} ${d.getMonth() + 1}/${d.getDate()}`;
};

/** Sum of the seven boxes. The driver never types this. */
export const totalHours = (days) =>
  (days || []).reduce((sum, d) => sum + (Number(d.hours) || 0), 0);

/** Whole hours, 0-24. Blank counts as 0; anything else is rejected. */
export const isValidHourEntry = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return true;
  if (!/^\d{1,2}$/.test(s)) return false;
  const n = Number(s);
  return n >= 0 && n <= DOT_HOURS_MAX_PER_DAY;
};
